// Canvas basics guide — the floating canvas toolbar (undo/redo, grid, Theme &
// Palette, training font), the multi-select align bar, Copy style and the
// right-click box menu. Locale-keyed like every guide (content.<lang> →
// { feature, title, sections }).

const canvasBasicsGuide = {
  id: "canvas-basics",
  icon: "text_fields",
  keywords: [
    "canvas",
    "tuval",
    "kanvas",
    "undo",
    "geri al",
    "redo",
    "grid",
    "ızgara",
    "theme",
    "tema",
    "palette",
    "palet",
    "background",
    "arka plan",
    "font",
    "yazı tipi",
    "training font",
    "eğitim fontu",
    "align",
    "hizala",
    "multi select",
    "çoklu seçim",
    "copy style",
    "stili kopyala",
    "format painter",
    "right click",
    "sağ tık",
    "bring to front",
    "öne getir",
    "send to back",
    "arkaya gönder",
    "layer",
    "katman",
    "lienzo",
    "deshacer",
    "rehacer",
    "cuadrícula",
    "paleta",
    "fondo",
    "fuente",
    "fuente de la formación",
    "alinear",
    "selección múltiple",
    "copiar estilo",
    "clic derecho",
    "traer al frente",
    "enviar al fondo",
    "capa",
    "resize",
    "resize text",
    "font size",
    "corner handle",
    "scale text",
    "yeniden boyutlandır",
    "boyutlandırma",
    "yazı boyutu",
    "köşe tutamacı",
    "cambiar tamaño",
    "tamaño de fuente",
    "tirador de esquina",
    "escalar texto",
  ],
  content: {
    en: {
      feature: "Canvas",
      title: "Canvas basics",
      sections: [
        {
          id: "toolbar",
          title: "The canvas toolbar",
          videoUrl: null,
          text:
            "A small toolbar floats at the top-left of the canvas with the scene-level actions:\n\n" +
            "- **Undo** and **Redo** step back and forward through your changes.\n" +
            "- The grid button (four squares) shows or hides a grid over the scene to help you line elements up.\n" +
            "- **Theme** opens the **Theme & Palette Selection** dialog.\n" +
            "- **Training Font** (the Aa button) changes the font of the text on the scene or across the training.\n\n" +
            "The toolbar hides while a copied style is waiting to be applied (see Copy style below).",
        },
        {
          id: "theme",
          title: "Theme & Palette",
          videoUrl: null,
          text:
            "In **Theme & Palette Selection**, pick a **Theme** from the list, then one of its **Palette** cards. Tick **Apply to all scenes** to use it on every scene instead of only the current one, then click **Apply**.\n\n" +
            "Choose **Custom** in the theme list to design the current scene's background yourself:\n\n" +
            "- **Color** — a solid color from the picker.\n" +
            "- **HTML / CSS** — your own background markup.\n" +
            "- **Background image** — paste an image URL or pick a file; choosing an image switches to a custom image background, with options for how it fills the scene and its opacity.\n\n" +
            "A custom background applies to the current scene only. **Remove Theme** clears the theme from the current scene, or from every scene when **Apply to all scenes** is ticked.",
        },
        {
          id: "font",
          title: "Changing the font",
          videoUrl: null,
          text:
            "Click **Training Font** in the canvas toolbar and choose how far the change goes:\n\n" +
            "- **This scene only** (the default) — the text boxes on the scene you're viewing.\n" +
            "- **Whole training** — every text box on every scene; it also becomes the training's default font.\n\n" +
            "Hover a font in the list to preview it live on the current scene, then click it to apply. A different font for a single box is set from that box's own text toolbar.\n\n" +
            "You can also right-click a text box and choose **Apply this font to all scenes** to spread that box's font across the whole training in one step.",
        },
        {
          id: "multi-select",
          title: "Selecting and aligning several elements",
          videoUrl: null,
          text:
            "Select several elements by holding **Shift** or **Ctrl** (**Cmd** on Mac) while clicking them, or drag a selection rectangle across the canvas. With two or more selected, an alignment bar appears next to the canvas toolbar:\n\n" +
            "- **Align Left**, **Align Right**, **Align Up** and **Align Down** line the elements up with the outermost one.\n" +
            "- **Align Center** lines them up on the middle element and needs at least three elements.\n\n" +
            "When every selected element is a text box, the bar also gets text controls — font, bold, italic, underline, text alignment, larger/smaller text, and **More options** for exact size, text color and highlight color — applied to all of them at once.",
        },
        {
          id: "copy-style",
          title: "Copy style",
          videoUrl: null,
          text:
            "Select an element and click **Copy style** (the paintbrush) in the right menu. A **Copied style** card appears on the canvas.\n\n" +
            "- Select the elements to restyle — one or many, on this scene or another — then click **Apply**. The button shows how many elements will change.\n" +
            "- A text box's style goes only to text boxes; any other element's style goes only to non-text elements. Others are skipped, and a message tells you how many.\n" +
            "- It copies the look, not the content, size or position: box background, border, corners, padding and opacity; text formatting; rotation and flip; entrance and exit effects; and image adjustments between elements of the same type. Each Apply is one undo step.\n" +
            "- The arrow on the card shows the copied values (**Show copied style**). The × (**Clear copied style**) ends copy mode, and so does clicking the top bar or the right menu.",
        },
        {
          id: "right-click",
          title: "The right-click menu",
          videoUrl: null,
          text:
            "Right-click an element on the canvas for quick actions. You only see the ones that fit that element:\n\n" +
            "- **Bring to Front**, **Forward**, **Backward** and **Send to Back** change its layer order on the scene.\n" +
            "- **Aidea Smart Elements** splits an image into separate layered elements you can edit one by one. It's greyed out for elements that can't be split.\n" +
            "- **Edit** opens an interactive scenario in design mode.\n" +
            "- **Apply this font to all scenes** appears on text boxes that have a font set and uses that box's font for the whole training.\n" +
            "- **Update position on all scenes** appears on brand assets placed from the Library's Brand tab (see the Brand kit guide).\n\n" +
            "Press Esc or click elsewhere to close the menu.",
        },
        {
          id: "resize-text",
          title: "Resizing text boxes",
          videoUrl: null,
          text:
            "How a text box reacts to resizing depends on which handle you drag:\n\n" +
            "- **A corner handle** scales the text together with the box — you see the new size live while dragging. In a text box the font grows or shrinks with the box's width; in a smart element all of its text scales together.\n" +
            "- **The bar in the middle of the bottom edge** changes only the box's height — the font size stays the same.\n\n" +
            "The text has limits: a text box's font never goes below 6 px, and a smart element's text stays within the same range as its **A** / **a** buttons (**Increase text size** / **Decrease text size**). Resizing and the font change it causes are undone together in one step.",
        },
      ],
    },
    tr: {
      feature: "Tuval",
      title: "Tuval temelleri",
      sections: [
        {
          id: "toolbar",
          title: "Tuval araç çubuğu",
          videoUrl: null,
          text:
            "Tuvalin sol üst köşesinde, sahne düzeyindeki işlemleri içeren küçük bir araç çubuğu bulunur:\n\n" +
            "- **Geri** ve **İleri** değişikliklerinizi geri alır ve yineler.\n" +
            "- **Izgara** düğmesi (dört kare), öğeleri hizalamanıza yardımcı olan ızgarayı sahnenin üzerinde gösterir veya gizler.\n" +
            "- **Tema**, **Tema & Palet Seçimi** penceresini açar.\n" +
            "- **Eğitim Fontu** (Aa düğmesi), sahnedeki veya tüm eğitimdeki metinlerin yazı tipini değiştirir.\n\n" +
            "Uygulanmayı bekleyen kopyalanmış bir stil varken araç çubuğu gizlenir (aşağıdaki Stili kopyala bölümüne bakın).",
        },
        {
          id: "theme",
          title: "Tema ve palet",
          videoUrl: null,
          text:
            "**Tema & Palet Seçimi** penceresinde listeden bir **Tema**, ardından o temanın **Palet** kartlarından birini seçin. Yalnızca geçerli sahne yerine her sahnede kullanmak için **Tüm sahnelere uygula**'yı işaretleyin ve **Uygula**'ya tıklayın.\n\n" +
            "Geçerli sahnenin arka planını kendiniz tasarlamak için tema listesinden **Özel**'i seçin:\n\n" +
            "- **Renk** — renk seçiciden düz bir renk.\n" +
            "- **HTML / CSS** — kendi arka plan kodunuz.\n" +
            "- **Arka Plan Resmi** — bir görsel URL'si yapıştırın veya dosya seçin; görsel seçmek özel görsel arka planına geçer, görselin sahneyi nasıl dolduracağı ve opaklığı için seçenekler sunar.\n\n" +
            "Özel arka plan yalnızca geçerli sahneye uygulanır. **Temayı Kaldır**, temayı geçerli sahneden kaldırır; **Tüm sahnelere uygula** işaretliyse tüm sahnelerden kaldırır.",
        },
        {
          id: "font",
          title: "Yazı tipini değiştirme",
          videoUrl: null,
          text:
            "Tuval araç çubuğunda **Eğitim Fontu**'na tıklayın ve değişikliğin kapsamını seçin:\n\n" +
            "- **Sadece bu sahne** (varsayılan) — görüntülediğiniz sahnedeki metin kutuları.\n" +
            "- **Tüm eğitim** — her sahnedeki tüm metin kutuları; seçilen yazı tipi eğitimin varsayılan yazı tipi de olur.\n\n" +
            "Listede bir yazı tipinin üzerine gelince geçerli sahnede canlı önizlenir; uygulamak için tıklayın. Tek bir kutunun yazı tipi, o kutunun kendi metin araç çubuğundan ayarlanır.\n\n" +
            "Ayrıca bir metin kutusuna sağ tıklayıp **Bu fontu tüm sahnelere uygula**'yı seçerek o kutunun yazı tipini tek adımda tüm eğitime yayabilirsiniz.",
        },
        {
          id: "multi-select",
          title: "Birden fazla öğeyi seçme ve hizalama",
          videoUrl: null,
          text:
            "**Shift** veya **Ctrl** (Mac'te **Cmd**) tuşuna basılı tutarak öğelere tıklayın ya da tuval üzerinde bir seçim dikdörtgeni sürükleyin. İki veya daha fazla öğe seçiliyken tuval araç çubuğunun yanında bir hizalama çubuğu belirir:\n\n" +
            "- **Sola Hizala**, **Sağa Hizala**, **Yukarı Hizala** ve **Aşağı Hizala** öğeleri en dıştaki öğeye göre hizalar.\n" +
            "- **Ortaya Hizala** öğeleri ortadaki öğeye göre hizalar ve en az üç öğe gerektirir.\n\n" +
            "Seçili öğelerin hepsi metin kutusuysa çubukta metin denetimleri de görünür — yazı tipi, kalın, italik, altı çizili, metin hizalama, metni büyütme/küçültme ve tam boyut, metin rengi ile vurgu rengi için **Daha fazla seçenek** — ve hepsine aynı anda uygulanır.",
        },
        {
          id: "copy-style",
          title: "Stili kopyala",
          videoUrl: null,
          text:
            "Bir öğe seçip sağ menüde **Stili kopyala**'ya (fırça simgesi) tıklayın. Tuvalde **Kopyalanan stil** kartı belirir.\n\n" +
            "- Yeniden biçimlendirilecek öğeleri seçin — bir veya birden çok, bu sahnede veya başka bir sahnede — ve **Uygula**'ya tıklayın. Düğme kaç öğenin değişeceğini gösterir.\n" +
            "- Bir metin kutusunun stili yalnızca metin kutularına, diğer öğelerin stili yalnızca metin olmayan öğelere uygulanır. Diğerleri atlanır ve kaç tanesinin atlandığı bir mesajla bildirilir.\n" +
            "- İçeriği, boyutu veya konumu değil görünümü kopyalar: kutu arka planı, kenarlık, köşeler, dolgu ve opaklık; metin biçimlendirmesi; döndürme ve çevirme; giriş ve çıkış efektleri; aynı türdeki öğeler arasında görsel ayarları. Her Uygula işlemi tek adımda geri alınabilir.\n" +
            "- Karttaki ok kopyalanan değerleri gösterir (**Kopyalanan stili göster**). × (**Stil panosunu temizle**) kopyalama modunu bitirir; üst çubuğa veya sağ menüye tıklamak da bitirir.",
        },
        {
          id: "right-click",
          title: "Sağ tık menüsü",
          videoUrl: null,
          text:
            "Hızlı işlemler için tuvaldeki bir öğeye sağ tıklayın. Yalnızca o öğeye uygun seçenekler görünür:\n\n" +
            "- **Öne Getir**, **İleri**, **Geri** ve **Arkaya Gönder** öğenin sahnedeki katman sırasını değiştirir.\n" +
            "- **Aidea Akıllı Öğeler** bir görseli tek tek düzenleyebileceğiniz ayrı katmanlı öğelere böler. Bölünemeyen öğelerde soluk görünür.\n" +
            "- **Düzenle** etkileşimli bir senaryoyu tasarım modunda açar.\n" +
            "- **Bu fontu tüm sahnelere uygula** yazı tipi ayarlanmış metin kutularında görünür ve o kutunun yazı tipini tüm eğitimde kullanır.\n" +
            "- **Tüm sahnelerde konumu güncelle**, Kütüphane'nin Marka sekmesinden yerleştirilen marka öğelerinde görünür (bkz. Marka kiti rehberi).\n\n" +
            "Menüyü kapatmak için Esc'ye basın veya başka bir yere tıklayın.",
        },
        {
          id: "resize-text",
          title: "Metin kutularını yeniden boyutlandırma",
          videoUrl: null,
          text:
            "Bir metin kutusunun boyutlandırmaya nasıl tepki verdiği, hangi tutamacı sürüklediğinize bağlıdır:\n\n" +
            "- **Köşe tutamacı** metni kutuyla birlikte ölçekler — sürüklerken yeni boyutu canlı olarak görürsünüz. Metin kutusunda yazı tipi, kutunun genişliğiyle birlikte büyür veya küçülür; akıllı öğede ise tüm metinler birlikte ölçeklenir.\n" +
            "- **Alt kenarın ortasındaki çubuk** yalnızca kutunun yüksekliğini değiştirir — yazı tipi boyutu aynı kalır.\n\n" +
            "Metnin sınırları vardır: metin kutusunda yazı tipi 6 px'in altına inmez; akıllı öğede metin, **A** / **a** düğmeleriyle (**Metin boyutunu büyüt** / **Metin boyutunu küçült**) ulaşılabilen aralığın içinde kalır. Boyutlandırma ve bunun yol açtığı yazı tipi değişikliği tek adımda birlikte geri alınır.",
        },
      ],
    },
    es: {
      feature: "Lienzo",
      title: "Conceptos básicos del lienzo",
      sections: [
        {
          id: "toolbar",
          title: "La barra de herramientas del lienzo",
          videoUrl: null,
          text:
            "En la esquina superior izquierda del lienzo flota una pequeña barra de herramientas con las acciones a nivel de escena:\n\n" +
            "- **Deshacer** y **Rehacer** retroceden y avanzan por tus cambios.\n" +
            "- El botón **Cuadrícula** (cuatro cuadrados) muestra u oculta una cuadrícula sobre la escena para ayudarte a alinear los elementos.\n" +
            "- **Tema** abre el diálogo **Selección de Tema y Paleta**.\n" +
            "- **Fuente de la Formación** (el botón Aa) cambia la fuente del texto de la escena o de toda la formación.\n\n" +
            "La barra de herramientas se oculta mientras hay un estilo copiado pendiente de aplicar (consulta Copiar estilo más abajo).",
        },
        {
          id: "theme",
          title: "Tema y paleta",
          videoUrl: null,
          text:
            "En **Selección de Tema y Paleta**, elige un tema de la lista **Tema** y después una de sus tarjetas de **Paleta**. Marca **Aplicar a todas las escenas** para usarlo en todas las escenas en lugar de solo en la actual y haz clic en **Aplicar**.\n\n" +
            "Elige **Personalizado** en la lista de temas para diseñar tú mismo el fondo de la escena actual:\n\n" +
            "- **Color** — un color sólido del selector.\n" +
            "- **HTML / CSS** — tu propio código para el fondo.\n" +
            "- **Imagen de Fondo** — pega la URL de una imagen o elige un archivo; al elegir una imagen se cambia a un fondo de imagen personalizado, con opciones para indicar cómo rellena la escena y su opacidad.\n\n" +
            "Un fondo personalizado solo se aplica a la escena actual. **Eliminar Tema** quita el tema de la escena actual, o de todas las escenas cuando **Aplicar a todas las escenas** está marcado.",
        },
        {
          id: "font",
          title: "Cambiar la fuente",
          videoUrl: null,
          text:
            "Haz clic en **Fuente de la Formación** en la barra de herramientas del lienzo y elige el alcance del cambio:\n\n" +
            "- **Solo esta escena** (la opción predeterminada) — los cuadros de texto de la escena que estás viendo.\n" +
            "- **Toda la formación** — todos los cuadros de texto de todas las escenas; además, pasa a ser la fuente predeterminada de la formación.\n\n" +
            "Pasa el ratón sobre una fuente de la lista para previsualizarla en directo en la escena actual y haz clic en ella para aplicarla. Para usar otra fuente en un solo cuadro, cámbiala desde la barra de herramientas de texto de ese cuadro.\n\n" +
            "También puedes hacer clic derecho en un cuadro de texto y elegir **Aplicar esta fuente a todas las escenas** para extender la fuente de ese cuadro a toda la formación en un solo paso.",
        },
        {
          id: "multi-select",
          title: "Seleccionar y alinear varios elementos",
          videoUrl: null,
          text:
            "Selecciona varios elementos manteniendo pulsada la tecla **Shift** o **Ctrl** (**Cmd** en Mac) mientras haces clic en ellos, o arrastra un rectángulo de selección sobre el lienzo. Con dos o más elementos seleccionados, aparece una barra de alineación junto a la barra de herramientas del lienzo:\n\n" +
            "- **Alinear a la izquierda**, **Alinear a la derecha**, **Alinear arriba** y **Alinear hacia abajo** alinean los elementos con el que está más hacia fuera.\n" +
            "- **Alineación central** los alinea con el elemento del medio y necesita al menos tres elementos.\n\n" +
            "Cuando todos los elementos seleccionados son cuadros de texto, la barra también incluye controles de texto (fuente, negrita, cursiva, subrayado, alineación del texto, texto más grande o más pequeño y **Más opciones** para el tamaño exacto, el color del texto y el color de resaltado) que se aplican a todos a la vez.",
        },
        {
          id: "copy-style",
          title: "Copiar estilo",
          videoUrl: null,
          text:
            "Selecciona un elemento y haz clic en **Copiar estilo** (el pincel) en el menú derecho. En el lienzo aparece una tarjeta **Estilo copiado**.\n\n" +
            "- Selecciona los elementos a los que quieres dar el nuevo estilo (uno o varios, en esta escena o en otra) y haz clic en **Aplicar**. El botón muestra cuántos elementos cambiarán.\n" +
            "- El estilo de un cuadro de texto solo se aplica a cuadros de texto; el de cualquier otro elemento, solo a elementos que no son de texto. Los demás se omiten y un mensaje te indica cuántos.\n" +
            "- Copia el aspecto, no el contenido, el tamaño ni la posición: fondo, borde, esquinas, relleno y opacidad del cuadro; formato del texto; rotación y volteo; efectos de entrada y salida; y ajustes de imagen entre elementos del mismo tipo. Cada Aplicar se deshace en un solo paso.\n" +
            "- La flecha de la tarjeta muestra los valores copiados (**Mostrar estilo copiado**). La × (**Borrar el estilo copiado**) termina el modo de copia, igual que hacer clic en la barra superior o en el menú derecho.",
        },
        {
          id: "right-click",
          title: "El menú contextual",
          videoUrl: null,
          text:
            "Haz clic derecho en un elemento del lienzo para acceder a acciones rápidas. Solo ves las que corresponden a ese elemento:\n\n" +
            "- **Traer al frente**, **Adelantar**, **Retroceder** y **Enviar al fondo** cambian su orden de capa en la escena.\n" +
            "- **Elementos Inteligentes Aidea** divide una imagen en elementos separados por capas que puedes editar uno a uno. Aparece atenuado en los elementos que no se pueden dividir.\n" +
            "- **Editar** abre un escenario interactivo en modo de diseño.\n" +
            "- **Aplicar esta fuente a todas las escenas** aparece en los cuadros de texto que tienen una fuente definida y usa la fuente de ese cuadro en toda la formación.\n" +
            "- **Actualizar posición en todas las escenas** aparece en los recursos de marca colocados desde la pestaña Marca de la Biblioteca (consulta la guía del kit de marca).\n\n" +
            "Pulsa Esc o haz clic en otro sitio para cerrar el menú.",
        },
        {
          id: "resize-text",
          title: "Cambiar el tamaño de los cuadros de texto",
          videoUrl: null,
          text:
            "Cómo reacciona un cuadro de texto al cambiar su tamaño depende del tirador que arrastres:\n\n" +
            "- **Un tirador de esquina** escala el texto junto con el cuadro; ves el nuevo tamaño en directo mientras arrastras. En un cuadro de texto, la fuente crece o se reduce con el ancho del cuadro; en un elemento inteligente, todo su texto se escala a la vez.\n" +
            "- **La barra en el centro del borde inferior** solo cambia la altura del cuadro; el tamaño de la fuente no cambia.\n\n" +
            "El texto tiene límites: en un cuadro de texto la fuente nunca baja de 6 px, y en un elemento inteligente el texto se mantiene dentro del mismo rango que sus botones **A** / **a** (**Aumentar tamaño de texto** / **Reducir tamaño de texto**). El cambio de tamaño y el cambio de fuente que provoca se deshacen juntos en un solo paso.",
        },
      ],
    },
  },
};

export default canvasBasicsGuide;
